import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, TextField, Chip, IconButton,
  Tooltip, CircularProgress, Alert, Divider, Paper,
} from "@mui/material";
import AddIcon        from "@mui/icons-material/Add";
import CheckIcon      from "@mui/icons-material/Check";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import DeleteIcon     from "@mui/icons-material/Delete";
import api from "../../utils/api";

const STATUS_COLOR = {
  pending: { bg:"rgba(251,146,60,0.1)", color:"#b45309",  label:"Pending" },
  met:     { bg:"rgba(34,197,94,0.1)",  color:"#16a34a",  label:"Met" },
  waived:  { bg:"rgba(0,0,0,0.06)",     color:"#6b778c",  label:"Waived" },
};

const ARConditionsPanel = ({ requestId, isAdmin }) => {
  const [conditions, setConditions] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [text,       setText]       = useState("");
  const [dueDate,    setDueDate]    = useState("");
  const [adding,     setAdding]     = useState(false);
  const [showAdd,    setShowAdd]    = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/architectural-requests/${requestId}/conditions`); setConditions(r.data||[]); }
    catch { console.error("Failed to load conditions"); }
    finally { setLoading(false); }
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!text.trim()) return;
    setAdding(true);
    try {
      await api.post(`/architectural-requests/${requestId}/conditions`, { condition_text: text.trim(), due_date: dueDate || null });
      setText(""); setDueDate(""); setShowAdd(false); load();
    } catch { console.error("Failed to add condition"); }
    finally { setAdding(false); }
  };

  const handleStatus = async (id, status) => {
    try { await api.patch(`/ar-conditions/${id}/status`, { status }); load(); }
    catch { console.error("Failed to update condition"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this condition?")) return;
    try { await api.delete(`/ar-conditions/${id}`); load(); }
    catch { console.error("Failed to delete condition"); }
  };

  const pending = conditions.filter(c => c.status === "pending");
  const done    = conditions.filter(c => c.status !== "pending");

  return (
    <Box>
      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", mb:2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight:700, color:"#223142" }}>Conditions</Typography>
          <Typography variant="body2" sx={{ color:"#6b778c" }}>
            Conditions the applicant must meet before the request can be closed.
            {pending.length > 0 && <strong style={{ color:"#b45309" }}> {pending.length} pending.</strong>}
          </Typography>
        </Box>
        {isAdmin && !showAdd && (
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setShowAdd(true)}
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, bgcolor:"#2f63ad", boxShadow:"none", "&:hover":{ bgcolor:"#1f4f94", boxShadow:"none" } }}>
            Add Condition
          </Button>
        )}
      </Box>

      {showAdd && isAdmin && (
        <Paper elevation={0} sx={{ p:2, mb:2, borderRadius:"12px", border:"1px solid rgba(47,99,173,0.12)", bgcolor:"rgba(47,99,173,0.02)" }}>
          <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1.5 }}>New Condition</Typography>
          <TextField fullWidth multiline rows={2} size="small" label="Condition *" value={text} onChange={e=>setText(e.target.value)}
            sx={{ mb:1.5, "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />
          <Box sx={{ display:"flex", gap:1, alignItems:"center" }}>
            <TextField size="small" type="date" label="Due Date (optional)" InputLabelProps={{ shrink:true }} value={dueDate} onChange={e=>setDueDate(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />
            <Button variant="contained" onClick={handleAdd} disabled={!text.trim()||adding}
              sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, bgcolor:"#2f63ad", boxShadow:"none" }}>
              {adding ? <CircularProgress size={18} color="inherit" /> : "Add"}
            </Button>
            <Button variant="outlined" onClick={()=>{ setShowAdd(false); setText(""); setDueDate(""); }}
              sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600 }}>Cancel</Button>
          </Box>
        </Paper>
      )}

      {loading ? <CircularProgress size={24} /> : conditions.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius:"10px" }}>No conditions have been set for this request.</Alert>
      ) : (
        <Box>
          {pending.length > 0 && (
            <Box sx={{ mb:2 }}>
              <Typography variant="caption" sx={{ fontWeight:700, color:"#b45309", textTransform:"uppercase", letterSpacing:0.6, display:"block", mb:1 }}>
                Pending ({pending.length})
              </Typography>
              {pending.map(c => (
                <Box key={c.id} sx={{ display:"flex", alignItems:"flex-start", gap:1.5, mb:1, p:1.5, borderRadius:"10px", border:"1px solid rgba(251,146,60,0.2)", bgcolor:"rgba(251,146,60,0.04)" }}>
                  <Box sx={{ flex:1 }}>
                    <Typography variant="body2" sx={{ fontWeight:600, color:"#223142" }}>{c.condition_text}</Typography>
                    {c.due_date && <Typography variant="caption" sx={{ color:"#9ca3af" }}>Due: {new Date(c.due_date).toLocaleDateString()}</Typography>}
                  </Box>
                  {isAdmin && (
                    <Box sx={{ display:"flex", gap:0.5 }}>
                      <Tooltip title="Mark as Met"><IconButton size="small" color="success" onClick={()=>handleStatus(c.id,"met")}><CheckIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Waive"><IconButton size="small" onClick={()=>handleStatus(c.id,"waived")}><RemoveCircleIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={()=>handleDelete(c.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {done.length > 0 && (
            <Box>
              <Divider sx={{ mb:1.5 }} />
              <Typography variant="caption" sx={{ fontWeight:700, color:"#6b778c", textTransform:"uppercase", letterSpacing:0.6, display:"block", mb:1 }}>
                Resolved ({done.length})
              </Typography>
              {done.map(c => {
                const sc = STATUS_COLOR[c.status] || STATUS_COLOR.pending;
                return (
                  <Box key={c.id} sx={{ display:"flex", alignItems:"center", gap:1.5, mb:0.75, p:1, borderRadius:"8px", opacity:0.75 }}>
                    <Box sx={{ flex:1 }}>
                      <Typography variant="body2" sx={{ color:"#374151", textDecoration: c.status==="met"?"line-through":"none" }}>{c.condition_text}</Typography>
                      {c.met_at && <Typography variant="caption" sx={{ color:"#9ca3af" }}>{c.status==="met"?"Met":"Waived"} on {new Date(c.met_at).toLocaleDateString()}{c.met_by_name ? ` by ${c.met_by_name}` : ""}</Typography>}
                    </Box>
                    <Chip size="small" label={sc.label} sx={{ fontWeight:700, fontSize:11, borderRadius:"8px", bgcolor:sc.bg, color:sc.color }} />
                    {isAdmin && <IconButton size="small" color="error" onClick={()=>handleDelete(c.id)}><DeleteIcon fontSize="small" /></IconButton>}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ARConditionsPanel;
